import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Statistic,
  Spin,
  Alert,
} from 'antd';
import { Bar, Pie, Line, Column } from '@ant-design/charts';
import {
  DatabaseOutlined,
  UserOutlined,
  MobileOutlined,
  GlobalOutlined,
  EyeOutlined,
  HddOutlined,
  PauseCircleOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { Service } from '@/apis';
import type { LogDetailResponse } from '@/apis';
import styles from './index.module.less';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface ChartData {
  category: string;
  value: number;
  date?: string;
}

// 生成模拟数据
const generateMockData = (): LogDetailResponse[] => {
  const brands = ['苹果', '华为', '小米', 'OPPO', 'vivo', '三星', '荣耀'];
  const channels = ['官网', 'App Store', 'Google Play', '第三方应用商店'];
  const eventTypes = ['1', '2', '3', '4']; // 展现、手动、停留、离开
  const deviceTypes = ['iOS', 'Android'];

  const mockData: LogDetailResponse[] = [];
  const now = Date.now();

  for (let i = 0; i < 200; i++) {
    const randomHour = Math.floor(Math.random() * 24);
    const randomDay = Math.floor(Math.random() * 7);
    const createTime =
      now - randomDay * 24 * 60 * 60 * 1000 - randomHour * 60 * 60 * 1000;

    mockData.push({
      id: i + 1,
      name: `点位${i + 1}`,
      t: eventTypes[Math.floor(Math.random() * eventTypes.length)],
      uid: `user_${Math.floor(Math.random() * 100)}`,
      cuid: `device_${Math.floor(Math.random() * 50)}`,
      puid: `https://example.com/page${i}`,
      mc: `型号${Math.floor(Math.random() * 20)}`,
      br: brands[Math.floor(Math.random() * brands.length)],
      dpi: '2.0',
      pl: deviceTypes[Math.floor(Math.random() * deviceTypes.length)],
      kv: '',
      channel: channels[Math.floor(Math.random() * channels.length)],
      inty: 'wifi',
      opty: '1',
      ip: `192.168.1.${Math.floor(Math.random() * 255)}`,
      referer: 'https://example.com',
      createTime,
    });
  }

  return mockData;
};

const LogDataDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<string>('all');
  const [dashboardData, setDashboardData] = useState<LogDetailResponse[]>([]);
  const [error, setError] = useState<string>('');
  const [isUsingMockData, setIsUsingMockData] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // 获取数据
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // 获取大量数据用于分析
      const response = await Service.logControllerGet(1, 1000, '');
      console.log('API Response:', response);

      // 检查响应结构
      if (response && response.data) {
        const data = response.data.rows || [];
        console.log('Raw Data:', data);
        console.log('Data length:', data.length);

        if (data.length > 0) {
          setDashboardData(data);
          setIsUsingMockData(false);
          console.log('Using real API data');
        } else {
          // 如果API返回空数据，使用模拟数据
          const mockData = generateMockData();
          console.log('API returned empty data, using mock data:', mockData);
          setDashboardData(mockData);
          setIsUsingMockData(true);
        }
      } else {
        // 如果响应结构不正确，使用模拟数据
        const mockData = generateMockData();
        console.log(
          'Invalid API response structure, using mock data:',
          mockData
        );
        setDashboardData(mockData);
        setIsUsingMockData(true);
      }
    } catch (err) {
      console.error('API Error:', err);
      // API调用失败时，使用模拟数据
      const mockData = generateMockData();
      console.log('API failed, using mock data:', mockData);
      setDashboardData(mockData);
      setIsUsingMockData(true);
      setError('API连接失败，当前显示模拟数据');
    } finally {
      setLoading(false);
    }
  };

  // 专门的数据刷新函数（不影响页面加载状态）
  const refreshData = async () => {
    try {
      setIsRefreshing(true);
      setError('');

      // 获取大量数据用于分析
      const response = await Service.logControllerGet(1, 1000, '');
      console.log('Refresh API Response:', response);

      // 检查响应结构
      if (response && response.data) {
        const data = response.data.rows || [];
        console.log('Refresh Raw Data:', data);

        if (data.length > 0) {
          setDashboardData(data);
          setIsUsingMockData(false);
          console.log('Refresh: Using real API data');
        } else {
          // 如果API返回空数据，使用模拟数据
          const mockData = generateMockData();
          console.log(
            'Refresh: API returned empty data, using mock data:',
            mockData
          );
          setDashboardData(mockData);
          setIsUsingMockData(true);
        }
      } else {
        // 如果响应结构不正确，使用模拟数据
        const mockData = generateMockData();
        console.log(
          'Refresh: Invalid API response structure, using mock data:',
          mockData
        );
        setDashboardData(mockData);
        setIsUsingMockData(true);
      }
    } catch (err) {
      console.error('Refresh API Error:', err);
      // API调用失败时，使用模拟数据
      const mockData = generateMockData();
      console.log('Refresh: API failed, using mock data:', mockData);
      setDashboardData(mockData);
      setIsUsingMockData(true);
      setError('API连接失败，当前显示模拟数据');
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // 过滤数据
  const filteredData = dashboardData.filter(item => {
    if (dateRange) {
      const itemDate = new Date(item.createTime);
      const startDate = new Date(dateRange[0]);
      const endDate = new Date(dateRange[1]);
      if (itemDate < startDate || itemDate > endDate) return false;
    }
    if (selectedChannel !== 'all' && item.channel !== selectedChannel)
      return false;
    return true;
  });

  // 事件类型分布数据
  const eventTypeData: ChartData[] = [
    {
      category: '展现',
      value: filteredData.filter(item => item.t === '1').length,
    },
    {
      category: '手动',
      value: filteredData.filter(item => item.t === '2').length,
    },
    {
      category: '停留',
      value: filteredData.filter(item => item.t === '3').length,
    },
    {
      category: '离开',
      value: filteredData.filter(item => item.t === '4').length,
    },
  ];

  // 设备品牌分布数据
  const brandData: ChartData[] = Object.entries(
    filteredData.reduce(
      (acc, item) => {
        const brand = item.br || '未知';
        acc[brand] = (acc[brand] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    )
  ).map(([brand, count]) => ({ category: brand, value: count }));

  // 渠道分布数据
  const channelData: ChartData[] = Object.entries(
    filteredData.reduce(
      (acc, item) => {
        const channel = item.channel || '未知';
        acc[channel] = (acc[channel] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    )
  ).map(([channel, count]) => ({ category: channel, value: count }));

  // 时间趋势数据（按小时统计）
  const hourlyData: ChartData[] = Array.from({ length: 24 }, (_, hour) => {
    const count = filteredData.filter(item => {
      // 处理时间戳，确保是数字类型
      const timestamp =
        typeof item.createTime === 'number'
          ? item.createTime
          : parseInt(String(item.createTime));
      const itemHour = new Date(timestamp).getHours();
      return itemHour === hour;
    }).length;
    return { category: `${hour.toString().padStart(2, '0')}:00`, value: count };
  });

  // 设备类型分布
  const deviceTypeData: ChartData[] = Object.entries(
    filteredData.reduce(
      (acc, item) => {
        const type = item.pl || '未知';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    )
  ).map(([type, count]) => ({ category: type, value: count }));

  // 获取所有渠道选项
  const channelOptions = Array.from(
    new Set(dashboardData.map(item => item.channel).filter(Boolean))
  );

  // 通用tooltip样式配置
  const createTooltipConfig = (title: string) => ({
    showTitle: true,
    title: title,
    formatter: (datum: { category: string; value: number }) => {
      return {
        name: datum.category,
        value: datum.value,
      };
    },
    domStyles: {
      'g2-tooltip': {
        backgroundColor: 'rgba(0, 0, 0, 0.85) !important',
        border: '1px solid #434343 !important',
        borderRadius: '6px !important',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3) !important',
        color: '#ffffff !important',
      },
      'g2-tooltip-title': {
        color: '#ffffff !important',
        fontWeight: 'bold !important',
      },
      'g2-tooltip-list-item-name': {
        color: '#ffffff !important',
      },
      'g2-tooltip-list-item-value': {
        color: '#40a9ff !important',
        fontWeight: 'bold !important',
      },
      'g2-tooltip-list-item': {
        color: '#ffffff !important',
      },
      'g2-tooltip-list-item-marker': {
        color: '#ffffff !important',
      },
    },
  });

  // 图表配置
  const pieConfig = {
    data: eventTypeData.filter(item => item.value > 0),
    angleField: 'value',
    colorField: 'category',
    radius: 0.8,
    height: 300,
    autoFit: true,
  };

  const barConfig = {
    data: brandData.slice(0, 10).filter(item => item.value > 0),
    xField: 'value',
    yField: 'category',
    label: {
      position: 'middle',
      style: {
        fill: '#FFFFFF',
        opacity: 0.6,
      },
    },
    height: 300,
    autoFit: true,
    scrollbar: {
      type: 'vertical',
    },
    color: '#1890ff',
    tooltip: {
      formatter: (datum: { value: number }) => {
        return { name: '设备数量', value: datum.value };
      },
    },
  };

  const lineConfig = {
    data: hourlyData,
    xField: 'category',
    yField: 'value',
    smooth: true,
    point: {
      size: 5,
      shape: 'diamond',
    },
    label: {
      style: {
        fill: '#aaa',
      },
    },
    height: 300,
    autoFit: true,
    color: '#52c41a',
    tooltip: {
      formatter: (datum: { category: string; value: number }) => {
        return { name: '事件数', value: datum.value || 0 };
      },
    },
    xAxis: {
      label: {
        rotate: -45,
      },
    },
  };

  const columnConfig = {
    data: channelData.filter(item => item.value > 0),
    xField: 'category',
    yField: 'value',
    label: {
      position: 'middle',
      style: {
        fill: '#FFFFFF',
        opacity: 0.6,
      },
    },
    height: 300,
    autoFit: true,
    color: '#722ed1',
    tooltip: {
      formatter: (datum: { value: number }) => {
        return { name: '事件数', value: datum.value };
      },
    },
  };

  const deviceTypeConfig = {
    data: deviceTypeData.filter(item => item.value > 0),
    xField: 'category',
    yField: 'value',
    label: {
      position: 'middle',
      style: {
        fill: '#FFFFFF',
        opacity: 0.6,
      },
    },
    height: 300,
    autoFit: true,
    color: '#eb2f96',
    tooltip: {
      formatter: (datum: { value: number }) => {
        return { name: '设备数量', value: datum.value };
      },
    },
  };

  // 计算总体统计
  const totalEvents = filteredData.length;
  const uniqueUsers = new Set(filteredData.map(item => item.uid)).size;
  const uniqueDevices = new Set(filteredData.map(item => item.cuid)).size;
  const uniqueChannels = new Set(filteredData.map(item => item.channel)).size;

  // 检查是否有数据
  const hasData = filteredData.length > 0;

  // 调试信息
  console.log('Dashboard Data Debug:', {
    totalData: dashboardData.length,
    filteredData: filteredData.length,
    eventTypeData,
    brandData,
    channelData,
    hourlyData: hourlyData.slice(0, 5), // 只显示前5条
  });

  if (loading) {
    return (
      <div className={styles.dashboardContainer}>
        <div className={styles.loadingContainer}>
          <Spin size='large' />
          <p>正在加载数据...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dashboardContainer}>
      {error && (
        <Alert
          message='数据加载错误'
          description={error}
          type='error'
          showIcon
          style={{ marginBottom: 16 }}
          action={
            <button onClick={fetchDashboardData} className={styles.retryButton}>
              重试
            </button>
          }
        />
      )}

      {isUsingMockData && (
        <Alert
          message='演示模式'
          description='当前显示的是模拟数据，用于演示图表功能。请确保API服务正常运行以获取真实数据。'
          type='info'
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 筛选器 */}
      <Card className={styles.filterCard}>
        <Row gutter={16} align='bottom'>
          <Col span={8}>
            <div className={styles.filterItem}>
              <label>时间范围：</label>
              <RangePicker
                style={{ width: '100%' }}
                placeholder={['开始日期', '结束日期']}
                onChange={(_, dateStrings) => {
                  setDateRange(
                    dateStrings[0] && dateStrings[1]
                      ? [dateStrings[0], dateStrings[1]]
                      : null
                  );
                }}
              />
            </div>
          </Col>
          <Col span={8}>
            <div className={styles.filterItem}>
              <label>渠道：</label>
              <Select
                style={{ width: '100%' }}
                value={selectedChannel}
                onChange={setSelectedChannel}
                placeholder='选择渠道'
              >
                <Option value='all'>全部渠道</Option>
                {channelOptions.map(channel => (
                  <Option key={channel} value={channel}>
                    {channel}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          <Col span={8}>
            <div className={styles.filterItem}>
              <label>&nbsp;</label>
              <button
                onClick={refreshData}
                disabled={isRefreshing}
                className={`${styles.refreshButton} ${isRefreshing ? styles.refreshButtonLoading : ''}`}
              >
                {isRefreshing ? '刷新中...' : '刷新数据'}
              </button>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 统计卡片 */}
      <Row gutter={16} className={styles.statsRow}>
        <Col span={6}>
          <Card>
            <Statistic
              title='总事件数'
              value={totalEvents}
              prefix={<DatabaseOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title='独立用户数'
              value={uniqueUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title='独立设备数'
              value={uniqueDevices}
              prefix={<MobileOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title='渠道数量'
              value={uniqueChannels}
              prefix={<GlobalOutlined />}
              valueStyle={{ color: '#eb2f96' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 图表区域 */}
      {isRefreshing && (
        <div
          style={{
            textAlign: 'center',
            marginBottom: '16px',
            color: '#1890ff',
          }}
        >
          <Spin size='small' /> 正在刷新数据...
        </div>
      )}
      <Row gutter={16} className={styles.chartsRow}>
        {/* 事件类型分布饼图 */}
        <Col span={12}>
          <Card title='事件类型分布' className={styles.chartCard}>
            {hasData && eventTypeData.some(item => item.value > 0) ? (
              <Pie
                data={pieConfig.data}
                angleField='value'
                colorField='category'
                radius={0.8}
                height={250}
                innerRadius={0.2}
                label={{
                  type: 'outer',
                  content: '{name}',
                }}
                legend={{
                  position: 'bottom',
                }}
                tooltip={createTooltipConfig('事件类型')}
              />
            ) : (
              <div className={styles.emptyData}>
                {!hasData ? '暂无数据' : '事件类型数据为空'}
              </div>
            )}
          </Card>
        </Col>

        {/* 设备品牌分布条形图 */}
        <Col span={12}>
          <Card title='设备品牌分布（Top 10）' className={styles.chartCard}>
            {hasData && brandData.some(item => item.value > 0) ? (
              <Bar {...barConfig} tooltip={createTooltipConfig('设备品牌')} />
            ) : (
              <div className={styles.emptyData}>暂无数据</div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={16} className={styles.chartsRow}>
        {/* 24小时事件趋势 */}
        <Col span={12}>
          <Card title='24小时事件趋势' className={styles.chartCard}>
            {hasData ? (
              <Line {...lineConfig} tooltip={createTooltipConfig('时间趋势')} />
            ) : (
              <div className={styles.emptyData}>暂无数据</div>
            )}
          </Card>
        </Col>

        {/* 渠道分布柱状图 */}
        <Col span={12}>
          <Card title='渠道分布' className={styles.chartCard}>
            {hasData && channelData.some(item => item.value > 0) ? (
              <Column
                {...columnConfig}
                tooltip={createTooltipConfig('渠道分布')}
              />
            ) : (
              <div className={styles.emptyData}>暂无数据</div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={16} className={styles.chartsRow}>
        {/* 设备类型分布 */}
        <Col span={12}>
          <Card title='设备类型分布' className={styles.chartCard}>
            {hasData && deviceTypeData.some(item => item.value > 0) ? (
              <Column
                {...deviceTypeConfig}
                tooltip={createTooltipConfig('设备类型')}
              />
            ) : (
              <div className={styles.emptyData}>暂无数据</div>
            )}
          </Card>
        </Col>

        {/* 事件类型详细统计 */}
        <Col span={12}>
          <Card title='事件类型详细统计' className={styles.chartCard}>
            <Row gutter={16}>
              <Col span={6}>
                <div className={styles.eventStatItem}>
                  <EyeOutlined
                    className={styles.eventIcon}
                    style={{ color: '#1890ff' }}
                  />
                  <div className={styles.eventStatValue}>
                    {filteredData.filter(item => item.t === '1').length}
                  </div>
                  <div className={styles.eventStatLabel}>展现</div>
                </div>
              </Col>
              <Col span={6}>
                <div className={styles.eventStatItem}>
                  <HddOutlined
                    className={styles.eventIcon}
                    style={{ color: '#52c41a' }}
                  />
                  <div className={styles.eventStatValue}>
                    {filteredData.filter(item => item.t === '2').length}
                  </div>
                  <div className={styles.eventStatLabel}>手动</div>
                </div>
              </Col>
              <Col span={6}>
                <div className={styles.eventStatItem}>
                  <PauseCircleOutlined
                    className={styles.eventIcon}
                    style={{ color: '#fa8c16' }}
                  />
                  <div className={styles.eventStatValue}>
                    {filteredData.filter(item => item.t === '3').length}
                  </div>
                  <div className={styles.eventStatLabel}>停留</div>
                </div>
              </Col>
              <Col span={6}>
                <div className={styles.eventStatItem}>
                  <LogoutOutlined
                    className={styles.eventIcon}
                    style={{ color: '#f5222d' }}
                  />
                  <div className={styles.eventStatValue}>
                    {filteredData.filter(item => item.t === '4').length}
                  </div>
                  <div className={styles.eventStatLabel}>离开</div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default LogDataDashboard;
